import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatINR, ROLE_LABELS } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid, AreaChart, Area,
} from "recharts";
import { TrendingUp, TrendingDown, Users, Home, Droplets, AlertCircle, FileText, Wallet, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const COLORS = ["#0F2942", "#E8702A", "#0D7650", "#7C3AED", "#0891B2", "#DC2626"];

function KpiCard({ label, value, sub, icon: Icon, tone = "primary", testid }) {
  const tones = {
    primary: "text-[#0F2942] dark:text-white bg-[#0F2942]/10",
    saffron: "text-[#E8702A] bg-[#E8702A]/10",
    green: "text-[#0D7650] bg-[#0D7650]/10",
    red: "text-red-600 bg-red-50 dark:bg-red-900/20",
  };
  return (
    <Card className="hover-lift" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-2 text-2xl font-heading font-bold tabular-nums text-slate-900 dark:text-white">{value}</div>
            {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}>
            <Icon className="w-5 h-5" strokeWidth={1.75} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OfficerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/summary")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data) return null;

  const { kpis, daily_collection, collections_by_module, top_wards, payment_methods } = data;

  return (
    <div className="space-y-6" data-testid="officer-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Overview</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900 dark:text-white">
            {ROLE_LABELS[user?.role]} Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">Welcome back, {user?.name}.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/reports"><Button variant="outline" data-testid="btn-view-reports">View Reports</Button></Link>
          <Link to="/gis"><Button className="bg-[#0F2942] hover:bg-[#1E3A5F]" data-testid="btn-view-map">GIS Map</Button></Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Collection" value={formatINR(kpis.total_collection)} sub="All-time revenue" icon={Wallet} tone="primary" testid="kpi-total-collection" />
        <KpiCard label="Monthly Collection" value={formatINR(kpis.monthly_collection)} sub="Last 30 days" icon={TrendingUp} tone="green" testid="kpi-monthly-collection" />
        <KpiCard label="Outstanding Dues" value={formatINR(kpis.outstanding)} sub={`${kpis.pending_demands} pending demands`} icon={AlertCircle} tone="saffron" testid="kpi-outstanding" />
        <KpiCard label="Properties" value={kpis.total_properties.toLocaleString()} sub={`${kpis.total_water_consumers} water consumers`} icon={Home} tone="primary" testid="kpi-total-properties" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="chart-daily-collection">
          <CardHeader><CardTitle className="text-base font-heading">Daily Collection (14 days)</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daily_collection}>
                <defs>
                  <linearGradient id="clr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0F2942" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#0F2942" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" fontSize={11} />
                <YAxis fontSize={11} tickFormatter={(v) => v > 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Area type="monotone" dataKey="amount" stroke="#0F2942" strokeWidth={2} fill="url(#clr)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="chart-collections-module">
          <CardHeader><CardTitle className="text-base font-heading">Collections by Module</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={collections_by_module.length ? collections_by_module : [{ module: "no_data", amount: 1 }]}
                  dataKey="amount" nameKey="module" outerRadius={80} label
                >
                  {collections_by_module.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="chart-top-wards">
          <CardHeader><CardTitle className="text-base font-heading">Top Wards by Properties</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={top_wards} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" fontSize={11} />
                <YAxis dataKey="ward" type="category" fontSize={11} />
                <Tooltip />
                <Bar dataKey="properties" fill="#0F2942" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="chart-payment-methods">
          <CardHeader><CardTitle className="text-base font-heading">Payment Method Mix</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={payment_methods}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="_id" fontSize={11} />
                <YAxis fontSize={11} tickFormatter={(v) => v > 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Bar dataKey="amount" fill="#E8702A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
